<template>
  <a-modal :title="modalTitle" :visible="visible" :width="900" :mask-closable="false" :confirm-loading="loading" @ok="handleSubmit" @cancel="handleCancel">
    <a-form-model ref="form" :model="formModel" :rules="rules" :label-col="{ span: 3 }" :wrapper-col="{ span: 20 }">
      <!-- 时间选择和会场类型 -->
      <a-row>
        <a-col :span="8">
          <a-form-model-item label="开始时间" prop="start_date" :label-col="{ span: 9 }" :wrapper-col="{ span: 14 }">
            <a-date-picker v-model="formModel.start_date" style="width: 100%" placeholder="请选择日期" format="YYYY-MM-DD" :disabled-date="disabledDate" @change="handleChange" />
          </a-form-model-item>
        </a-col>
        <a-col :span="4">
          <a-form-model-item prop="start_time_picker" :label-col="{ span: 0 }" :wrapper-col="{ span: 24 }">
            <a-time-picker v-model="formModel.start_time_picker" style="width: 100%" placeholder="请选择时间" format="HH:mm" :minute-step="15" :allow-clear="false" @change="handleChange" />
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="会场类型" prop="activity" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
            <a-select v-model="formModel.activity" placeholder="请选择会场类型" :loading="activityLoading" @change="handleChange">
              <a-select-option v-for="option in activityOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </a-select-option>
            </a-select>
          </a-form-model-item>
        </a-col>
      </a-row>

      <!-- 会场名称 -->
      <a-form-model-item label="会场名称" prop="name">
        <a-input-group compact>
          <a-input v-model="formModel.name" placeholder="请输入会场名称" :max-length="50" style="width: calc(100% - 64px)" />
          <a-button type="primary" :disabled="!canGenerateName" title="根据开始时间和会场类型自动生成会场名称" @click="generateVenueName"> 生成 </a-button>
        </a-input-group>
      </a-form-model-item>

      <!-- 竞拍需求选择 -->
      <a-form-model-item label="竞拍需求" prop="business_requirements">
        <div class="selection-container">
          <div class="selection-header">
            <a-input-search v-model="requirementSearchText" placeholder="搜索需求..." style="width: 200px" size="small" allow-clear />
            <div class="selection-actions">
              <a-button size="small" type="link" @click="loadVenueProducts"> 刷新 </a-button>
              <a-divider type="vertical" />
              <a-button size="small" type="link" @click="selectAllRequirements">全选</a-button>
              <a-divider type="vertical" />
              <a-button size="small" type="link" @click="clearAllRequirements">清空</a-button>
              <a-divider type="vertical" />
              <span class="selection-count"> 已选 {{ selectedRequirements.length }} / {{ filteredRequirements.length }} 项 </span>
            </div>
          </div>
          <div class="requirement-checkbox-group-container">
            <a-spin :spinning="venueProductLoading">
              <div v-if="!filteredRequirements.length" class="empty-state">
                <a-empty description="暂无需求数据" />
              </div>
              <a-checkbox-group v-else v-model="formModel.business_requirements" @change="handleRequirementChange">
                <a-list size="small" :data-source="filteredRequirements">
                  <a-list-item slot="renderItem" slot-scope="item">
                    <a-checkbox :value="item.id" class="requirement-checkbox">
                      <span class="requirement-tag">BR-{{ item.id }}</span>
                      <a class="requirement-title custom-link" :href="requirementLink(item)" target="_blank" :title="item.title">
                        {{ item.title }}
                      </a>
                      <span v-if="item.status === 4" class="requirement-tag requirement-unsold"> 流拍 </span>
                    </a-checkbox>
                    <span slot="actions" class="requirement-price"> {{ item.price }}<span class="requirement-price-unit">BRP</span> </span>
                  </a-list-item>
                </a-list>
              </a-checkbox-group>
            </a-spin>
          </div>
        </div>
      </a-form-model-item>

      <!-- 参与团队选择 -->
      <a-form-model-item label="参与团队" prop="teams">
        <div class="selection-container">
          <div class="selection-header">
            <a-input-search v-model="teamSearchText" placeholder="搜索团队..." style="width: 200px" size="small" allow-clear />
            <div class="selection-actions">
              <a-button size="small" type="link" @click="loadTeams"> 刷新 </a-button>
              <a-divider type="vertical" />
              <a-button size="small" type="link" @click="selectAllTeams">全选</a-button>
              <a-divider type="vertical" />
              <a-button size="small" type="link" @click="clearAllTeams">清空</a-button>
              <a-divider type="vertical" />
              <span class="selection-count"> 已选 {{ selectedTeams.length }} / {{ filteredTeams.length }} 项 </span>
            </div>
          </div>
          <div class="checkbox-group-container">
            <a-spin :spinning="teamLoading">
              <div v-if="!filteredTeams.length" class="empty-state">
                <a-empty description="暂无团队数据" />
              </div>
              <a-checkbox-group v-else v-model="formModel.teams" class="checkbox-grid-layout" @change="handleTeamChange">
                <span v-for="team in filteredTeams" :key="team.id" class="checkbox-grid-layout-item">
                  <a-checkbox :value="team.id" class="team-checkbox-item">
                    <span class="team-name" :title="team.description">{{ team.name }}</span>
                  </a-checkbox>
                </span>
              </a-checkbox-group>
            </a-spin>
          </div>
        </div>
      </a-form-model-item>

      <!-- 备注 -->
      <a-form-model-item label="备注" prop="description">
        <a-textarea v-model="formModel.description" placeholder="请输入备注信息" :rows="3" :max-length="200" show-count />
      </a-form-model-item>
    </a-form-model>
  </a-modal>
</template>

<script>
import { teamList } from '@/api/system/team';
import { venueActivity } from '@/utils/venueConstants';
import { getVenueDetail, venueCreate, venueModify, venueProductQuery } from '@/api/business/venue';
import moment from 'moment';

export default {
  name: 'VenueModal',
  data() {
    return {
      // 模态框状态
      visible: false,
      loading: false,
      isEdit: false,
      currentId: null,

      // 加载状态
      venueProductLoading: false,
      teamLoading: false,
      activityLoading: false,

      // 表单数据
      formModel: this.getInitialFormModel(),

      // 数据源
      productData: [],
      teamList: [],

      // 搜索文本
      requirementSearchText: '',
      teamSearchText: '',

      // 选中状态
      selectedRequirements: [],
      selectedTeams: [],

      // 选项数据
      activityOptions: Object.values(venueActivity),

      // 表单验证规则
      rules: {
        name: [
          { required: true, message: '请输入会场名称' },
          { min: 2, max: 50, message: '会场名称长度在 2 到 50 个字符' }
        ],
        activity: [{ required: true, message: '请选择会场类型' }],
        start_date: [{ required: true, message: '请选择开始日期' }],
        start_time_picker: [{ required: true, message: '请选择开始时间' }],
        business_requirements: [
          { required: true, message: '请选择竞拍需求' },
          { type: 'array', min: 1, message: '至少选择一个竞拍需求' }
        ],
        teams: [
          { required: true, message: '请选择参与团队' },
          { type: 'array', min: 1, message: '至少选择一个参与团队' }
        ],
        description: [{ max: 200, message: '备注信息不能超过200个字符' }]
      }
    };
  },

  computed: {
    modalTitle() {
      return this.isEdit ? '编辑会场' : '添加会场';
    },

    canGenerateName() {
      return this.formModel.start_date && this.formModel.start_time_picker && this.formModel.activity;
    },

    requirementLink() {
      return record => {
        return this.$router.resolve({
          name: 'page:business:requirement:detail',
          params: { id: record.id }
        }).href;
      };
    },

    filteredRequirements() {
      if (!this.requirementSearchText) {
        return this.productData;
      }
      const searchText = this.requirementSearchText.toLowerCase();
      return this.productData.filter(req => req.title.toLowerCase().includes(searchText) || (req.code && req.code.toLowerCase().includes(searchText)));
    },

    filteredTeams() {
      if (!this.teamSearchText) {
        return this.teamList;
      }
      const searchText = this.teamSearchText.toLowerCase();
      return this.teamList.filter(team => team.name.toLowerCase().includes(searchText) || (team.description && team.description.toLowerCase().includes(searchText)));
    }
  },

  methods: {
    // ==================== 公共方法 ====================
    getInitialFormModel() {
      return {
        name: '',
        start_date: null,
        start_time_picker: null,
        activity: null,
        business_requirements: [],
        teams: [],
        description: ''
      };
    },

    resetForm() {
      this.formModel = this.getInitialFormModel();
      this.selectedRequirements = [];
      this.selectedTeams = [];
      this.requirementSearchText = '';
      this.teamSearchText = '';
      this.clearFormValidation();
    },

    clearFormValidation() {
      if (this.$refs.form) {
        this.$refs.form.clearValidate();
      }
    },

    // ==================== 模态框操作 ====================
    openAdd() {
      this.visible = true;
      this.isEdit = false;
      this.currentId = null;
      this.resetForm();
      this.setDefaultDateTime();
      this.loadInitialData();
    },

    openEdit(id) {
      this.visible = true;
      this.isEdit = true;
      this.currentId = id;
      this.resetForm();
      this.loadInitialData();
      this.loadVenueDetail(id);
    },

    handleCancel() {
      this.visible = false;
      this.isEdit = false;
      this.currentId = null;
      this.productData = [];
      this.teamList = [];
      this.resetForm();
    },

    // ==================== 数据加载 ====================
    async loadInitialData() {
      await Promise.all([this.loadVenueProducts(), this.loadTeams()]);
    },

    async loadVenueDetail(id) {
      try {
        this.loading = true;
        const res = await getVenueDetail({ id });
        const data = res.data;

        this.$nextTick(() => {
          const requirementIds = data.products ? data.products.map(r => r.source_id) : [];
          const teamIds = data.participants ? data.participants.map(t => t.id) : [];
          const startTime = data.start_time ? moment(data.start_time) : null;

          this.formModel = {
            name: data.name || '',
            activity: data.activity,
            start_date: startTime,
            start_time_picker: startTime,
            business_requirements: requirementIds,
            teams: teamIds,
            description: data.description || ''
          };

          this.selectedRequirements = requirementIds;
          this.selectedTeams = teamIds;
        });
      } catch (error) {
        this.$message.error('加载会场详情失败');
        console.error('加载会场详情失败:', error);
      } finally {
        this.loading = false;
      }
    },

    async loadVenueProducts() {
      try {
        this.venueProductLoading = true;
        const res = await venueProductQuery({
          status: [135, 136, 137],
          venueId: this.currentId
        });
        this.productData = res.data.items || [];
      } catch (error) {
        this.$message.error('加载竞拍需求失败');
        console.error('加载竞拍需求失败:', error);
      } finally {
        this.venueProductLoading = false;
      }
    },

    async loadTeams() {
      try {
        this.teamLoading = true;
        const res = await teamList({
          category_list: [1, 3],
          page_number: 1,
          page_size: 1000
        });
        this.teamList = res.data.items || [];
      } catch (error) {
        this.$message.error('加载团队列表失败');
        console.error('加载团队列表失败:', error);
      } finally {
        this.teamLoading = false;
      }
    },

    // ==================== 表单提交 ====================
    handleSubmit() {
      this.$refs.form.validate(async valid => {
        if (valid) {
          try {
            this.loading = true;
            const formData = this.buildSubmitData();
            const request = this.isEdit ? venueModify({ id: this.currentId, ...formData }) : venueCreate(formData);

            await request;
            this.$message.success(this.isEdit ? '修改成功' : '创建成功');
            this.handleCancel();
            this.$emit('close');
          } catch (error) {
            this.$message.error(error.message || (this.isEdit ? '修改失败' : '创建失败'));
          } finally {
            this.loading = false;
          }
        }
      });
    },

    buildSubmitData() {
      return {
        name: this.formModel.name.trim(),
        start_time: `${this.formModel.start_date.format('YYYY-MM-DD')} ${this.formModel.start_time_picker.format('HH:mm')}:00`,
        activity: this.formModel.activity,
        business_requirements: this.formModel.business_requirements,
        teams: this.formModel.teams,
        description: this.formModel.description.trim()
      };
    },

    // ==================== 选择操作 ====================
    handleRequirementChange(checkedValues) {
      this.selectedRequirements = checkedValues;
    },

    handleTeamChange(checkedValues) {
      this.selectedTeams = checkedValues;
    },

    selectAllRequirements() {
      const allIds = this.filteredRequirements.map(req => req.id);
      this.formModel.business_requirements = allIds;
      this.selectedRequirements = allIds;
    },

    clearAllRequirements() {
      this.formModel.business_requirements = [];
      this.selectedRequirements = [];
    },

    selectAllTeams() {
      const allIds = this.filteredTeams.map(team => team.id);
      this.formModel.teams = allIds;
      this.selectedTeams = allIds;
    },

    clearAllTeams() {
      this.formModel.teams = [];
      this.selectedTeams = [];
    },

    // ==================== 时间处理 ====================
    setDefaultDateTime() {
      const now = moment();
      const today = moment().startOf('day');

      // 默认日期为今天
      this.formModel.start_date = today;

      // 计算下一个合适的时间点（15分钟间隔）
      const currentMinute = now.minute();
      const nextQuarter = Math.ceil(currentMinute / 15) * 15;

      let defaultTime;
      if (nextQuarter >= 60) {
        defaultTime = moment().add(1, 'hour').startOf('hour');
      } else {
        defaultTime = moment().startOf('hour').add(nextQuarter, 'minute');
      }

      // 如果计算出的时间已经过了，则设置为明天同一时间
      if (defaultTime.isBefore(now)) {
        defaultTime = defaultTime.add(1, 'day');
        this.formModel.start_date = defaultTime.clone().startOf('day');
      }

      this.formModel.start_time_picker = defaultTime;
    },

    disabledDate(current) {
      // 禁用过去的日期
      return current && current < moment().startOf('day');
    },

    // ==================== 会场名称生成 ====================
    generateVenueName() {
      if (!this.canGenerateName) {
        this.$message.warning('请先选择开始时间和会场类型');
        return;
      }

      const date = this.formModel.start_date.format('YYYY/MM/DD');
      const time = this.formModel.start_time_picker.format('HH:mm');
      const activityLabel = this.activityOptions.find(opt => opt.value === this.formModel.activity)?.label || '';
      this.formModel.name = `${date} ${time} ${activityLabel}会场`;
    },

    // 自动生成会场名称（当字段变化时）
    autoGenerateVenueName() {
      // 只有在新增模式下才自动生成
      if (!this.isEdit && this.canGenerateName) {
        this.generateVenueName();
      }
    },

    // ==================== 表单变化事件处理 ====================
    handleChange() {
      this.$nextTick(() => {
        this.autoGenerateVenueName();
      });
    }
  }
};
</script>

<style lang="less" scoped>
// ==================== 基础样式 ====================
.selection-container {
  padding: 12px;
  background: #fafafa;
  border-radius: 6px;
  border: 1px solid #e8e8e8;
  transition: all 0.3s ease;

  &:hover {
    border-color: #d9d9d9;
  }

  .selection-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}

// ==================== 竞拍需求样式 ====================
.requirement-checkbox-group-container {
  max-height: 280px;
  overflow-y: auto;
  background-color: #fff;
  border-radius: 6px;
  border: 1px solid #e8e8e8;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;

    &:hover {
      background: #a8a8a8;
    }
  }

  .requirement-checkbox {
    width: 100%;
    padding: 0;

    :deep(.ant-checkbox) {
      margin-right: 8px;
    }
  }

  .requirement-tag {
    font-size: 11px;
    color: #1890ff;
    background-color: #e6f7ff;
    padding: 2px 6px;
    border-radius: 10px;
    margin-right: 6px;
    font-weight: 500;

    &.requirement-unsold {
      color: #722ed1;
      background-color: #f9f0ff;
      margin-left: 4px;
    }
  }

  .requirement-title {
    font-size: 13px;
    line-height: 1.4;
    color: #333;
    text-decoration: none;
    max-width: 400px;
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;

    &:hover {
      color: #1890ff;
    }
  }
  .ant-checkbox-group {
    display: block;
  }

  .requirement-price {
    font-size: 14px;
    color: #1890ff;
    font-weight: 600;

    .requirement-price-unit {
      font-size: 10px;
      color: #666;
      margin-left: 2px;
      font-weight: normal;
    }
  }
}

// ==================== 团队选择样式 ====================
.checkbox-group-container {
  max-height: 280px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;

    &:hover {
      background: #a8a8a8;
    }
  }

  .empty-state {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 120px;
    color: #999;
  }

  .checkbox-grid-layout {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    .checkbox-grid-layout-item {
      width: 24%;
      min-width: 120px;
    }

    .team-checkbox-item {
      width: 100%;
      padding: 10px 12px;
      border: 1px solid #e8e8e8;
      border-radius: 6px;
      background: #fff;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      cursor: pointer;

      &:hover {
        border-color: #1890ff;
        box-shadow: 0 2px 6px rgba(24, 144, 255, 0.1);
      }

      :deep(.ant-checkbox) {
        margin-right: 8px;

        .ant-checkbox-inner {
          border-radius: 3px;
        }
      }

      .team-name {
        font-size: 13px;
        color: #333;
        font-weight: 500;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1;
        line-height: 1.3;
      }

      // 选中状态样式
      &:has(.ant-checkbox-checked),
      :deep(.ant-checkbox-checked) ~ .team-name {
        border-color: #1890ff;
        background-color: #f6f9ff;
        color: #1890ff;
      }
    }
  }
}

// ==================== 全局样式优化 ====================
:deep(.ant-modal-body) {
  padding: 12px;
}

:deep(.ant-form-item) {
  margin-bottom: 12px;
}

:deep(.ant-input),
:deep(.ant-select-selector),
:deep(.ant-picker) {
  border-radius: 6px;

  &:focus,
  &:hover {
    border-color: #1890ff;
  }
}

:deep(.ant-spin-container) {
  position: relative;
}

:deep(.ant-list-item) {
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
}

// ==================== 响应式优化 ====================
@media (max-width: 768px) {
  .selection-container {
    .selection-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;

      .selection-actions {
        width: 100%;
        justify-content: space-between;
      }
    }
  }

  .checkbox-grid-layout {
    .checkbox-grid-layout-item {
      width: calc(50% - 4px);
    }
  }
}
</style>
